// src/utils/productionOrderActivityLogger.js

import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Comprehensive Production Order Activity Logger
 * Tracks all production order-related actions with detailed information
 */

// Activity Types
export const PRODUCTION_ACTIVITY_TYPES = {
  ORDER_CREATED: 'uretim_emri_olusturuldu',
  ORDER_UPDATED: 'uretim_emri_guncellendi',
  ORDER_DELETED: 'uretim_emri_silindi',
  ORDER_STATUS_CHANGED: 'uretim_emri_durum_degisti',
  STEP_COMPLETED: 'uretim_adimi_tamamlandi',
  STEP_UPDATED: 'uretim_adimi_guncellendi',
  MATERIAL_DEDUCTED: 'hammadde_cikarildi',
  PRODUCT_ADDED: 'urun_stoga_eklendi',
  WASTE_RECORDED: 'fire_kaydedildi',
  QUALITY_CONTROL_SENT: 'kalite_kontrole_gonderildi',
  WAREHOUSE_DISPATCHED: 'depoya_sevk_edildi'
};

// Activity Messages in Turkish
export const PRODUCTION_ACTIVITY_MESSAGES = {
  [PRODUCTION_ACTIVITY_TYPES.ORDER_CREATED]: 'Üretim emri oluşturuldu',
  [PRODUCTION_ACTIVITY_TYPES.ORDER_UPDATED]: 'Üretim emri güncellendi',
  [PRODUCTION_ACTIVITY_TYPES.ORDER_DELETED]: 'Üretim emri silindi',
  [PRODUCTION_ACTIVITY_TYPES.ORDER_STATUS_CHANGED]: 'Üretim emri durumu değiştirildi',
  [PRODUCTION_ACTIVITY_TYPES.STEP_COMPLETED]: 'Üretim adımı tamamlandı',
  [PRODUCTION_ACTIVITY_TYPES.STEP_UPDATED]: 'Üretim adımı güncellendi',
  [PRODUCTION_ACTIVITY_TYPES.MATERIAL_DEDUCTED]: 'Hammaddeler stoktan çıkarıldı',
  [PRODUCTION_ACTIVITY_TYPES.PRODUCT_ADDED]: 'Ürün stoğa eklendi',
  [PRODUCTION_ACTIVITY_TYPES.WASTE_RECORDED]: 'Fire kaydedildi',
  [PRODUCTION_ACTIVITY_TYPES.QUALITY_CONTROL_SENT]: 'Kalite kontrole gönderildi',
  [PRODUCTION_ACTIVITY_TYPES.WAREHOUSE_DISPATCHED]: 'Depoya sevk edildi'
};

/**
 * Log production order activity to Firestore
 * @param {object} params - Activity parameters
 * @param {string} params.companyId - Company ID
 * @param {string} params.orderId - Order ID
 * @param {object} params.user - Current user object
 * @param {string} params.activityType - Type of activity (from PRODUCTION_ACTIVITY_TYPES)
 * @param {object} params.details - Additional details about the activity
 * @param {object} params.batch - Optional Firestore batch to use
 * @returns {Promise} - Promise that resolves when activity is logged
 */
export const logProductionActivity = async ({
  companyId,
  orderId,
  user,
  activityType,
  details = {},
  batch = null
}) => {
  if (!companyId || !orderId || !user || !activityType) {
    console.error('Missing required parameters for production activity logging');
    return;
  }

  try {
    const shouldCommitBatch = !batch;
    const activityBatch = batch || writeBatch(db);

    // Create activity log entry
    const activityRef = doc(collection(db, 'companies', companyId, 'productionOrders', orderId, 'activities'));
    
    const activityData = {
      // User Information
      userId: user.uid,
      userEmail: user.email,
      userName: user.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
      userRole: details.userRole || 'kullanıcı',
      
      // Activity Information
      activityType,
      activityMessage: PRODUCTION_ACTIVITY_MESSAGES[activityType] || 'Bilinmeyen işlem',
      
      // Timestamp
      timestamp: serverTimestamp(),
      formattedDate: new Date().toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      
      // Order Context
      orderId,
      orderNumber: details.orderNumber || orderId.substring(0, 6),
      
      // Activity Details
      details: {
        ...details,
        ipAddress: details.ipAddress || 'N/A',
        userAgent: details.userAgent || navigator?.userAgent?.substring(0, 100) || 'N/A'
      },
      
      // Additional Metadata
      actionSource: 'web_app',
      version: '1.0'
    };

    activityBatch.set(activityRef, activityData);

    // Also log to company-wide activity log for admin tracking
    const companyActivityRef = doc(collection(db, 'companies', companyId, 'activityLog'));
    const companyActivityData = {
      ...activityData,
      entityType: 'productionOrder',
      entityId: orderId,
      action: `production_${activityType}`,
      description: `${PRODUCTION_ACTIVITY_MESSAGES[activityType]} - Üretim Emri #${details.orderNumber || orderId.substring(0, 6)}`
    };

    activityBatch.set(companyActivityRef, companyActivityData);

    if (shouldCommitBatch) {
      await activityBatch.commit();
    }

    console.log(`Production activity logged: ${activityType} for order ${orderId}`);
  } catch (error) {
    console.error('Error logging production activity:', error);
  }
};

/**
 * Helper functions for specific production order activities
 */

export const logProductionOrderCreated = async (companyId, orderId, user, orderData) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.ORDER_CREATED,
    details: {
      productName: orderData.product,
      quantity: orderData.quantity,
      orderNumber: orderData.orderNumber || orderId.substring(0, 6),
      userRole: orderData.userRole || 'kullanıcı'
    }
  });
};

export const logProductionOrderUpdated = async (companyId, orderId, user, changes, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.ORDER_UPDATED,
    details: {
      changes,
      changeCount: Object.keys(changes).length,
      fieldsChanged: Object.keys(changes).join(', '),
      userRole: changes.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logProductionOrderDeleted = async (companyId, orderId, user, orderData, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.ORDER_DELETED,
    details: {
      productName: orderData.product,
      quantity: orderData.quantity,
      deleteReason: orderData.deleteReason || 'Kullanıcı talebi',
      userRole: orderData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logStepCompleted = async (companyId, orderId, user, stepData, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.STEP_COMPLETED,
    details: {
      stepName: stepData.name,
      stepIndex: stepData.index,
      completedQuantity: stepData.completedQuantity,
      totalQuantity: stepData.totalQuantity,
      workCenter: stepData.workCenter,
      userRole: stepData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logMaterialDeducted = async (companyId, orderId, user, materialData, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.MATERIAL_DEDUCTED,
    details: {
      materialName: materialData.name,
      deductedQuantity: materialData.quantity,
      unit: materialData.unit,
      userRole: materialData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logProductAdded = async (companyId, orderId, user, productData, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.PRODUCT_ADDED,
    details: {
      productName: productData.name,
      addedQuantity: productData.quantity,
      unit: productData.unit,
      userRole: productData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logWasteRecorded = async (companyId, orderId, user, wasteData, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.WASTE_RECORDED,
    details: {
      productName: wasteData.name,
      wasteQuantity: wasteData.quantity,
      unit: wasteData.unit,
      stepName: wasteData.stepName,
      userRole: wasteData.userRole || 'kullanıcı'
    },
    batch
  });
};

export const logStatusChanged = async (companyId, orderId, user, statusData, batch = null) => {
  await logProductionActivity({
    companyId,
    orderId,
    user,
    activityType: PRODUCTION_ACTIVITY_TYPES.ORDER_STATUS_CHANGED,
    details: {
      oldStatus: statusData.oldStatus,
      newStatus: statusData.newStatus,
      reason: statusData.reason || 'Manuel değişiklik',
      userRole: statusData.userRole || 'kullanıcı'
    },
    batch
  });
};

const productionOrderActivityLogger = {
  logProductionActivity,
  logProductionOrderCreated,
  logProductionOrderUpdated,
  logProductionOrderDeleted,
  logStepCompleted,
  logMaterialDeducted,
  logProductAdded,
  logWasteRecorded,
  logStatusChanged,
  PRODUCTION_ACTIVITY_TYPES,
  PRODUCTION_ACTIVITY_MESSAGES
};

export default productionOrderActivityLogger;